/* A booking's page (prototype .crumbs, .bdHead, .bdWhen, .bdGrid, .ledger, .prep, .chatBlock, .bdSide, .sideNote, .reqNote), in the telify layer. */
@layer telify {
  .page {
    padding-bottom: 96px;
  }

  .crumbs {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 26px 0 0;
    font-size: 13.5px;
    color: var(--text-tertiary);
  }

  .sep {
    color: var(--text-muted);
  }

  .here {
    color: var(--text-secondary);
  }

  .head {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 32px;
    margin-top: 30px;
    padding-bottom: 30px;
    border-bottom: var(--line-strong);
  }

  .h1 {
    margin-top: 10px;
    font-family: var(--font-display);
    font-weight: 500;
    font-size: 56px;
    letter-spacing: -0.015em;
    line-height: 1.05;
  }

  .with {
    margin-top: 14px;
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 16px;
    color: var(--text-secondary);
  }

  .with img {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    object-fit: cover;
  }

  .with b {
    font-weight: 600;
    color: var(--accent);
  }

  .when {
    text-align: right;
    padding-bottom: 6px;
  }

  .when b {
    display: block;
    font-family: var(--font-display);
    font-weight: 500;
    font-size: 30px;
    line-height: 1.1;
    color: var(--accent);
  }

  .whenDim b {
    color: var(--text-tertiary);
    font-size: 24px;
  }

  .st {
    margin-top: 10px;
    font-size: 14.5px;
  }

  .stOff::before {
    background: transparent;
    box-shadow: inset 0 0 0 1.5px currentColor;
  }

  .grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 360px;
    gap: 72px;
    margin-top: 8px;
    align-items: start;
  }

  /* The app's notice (BookingDetailPage .notice): an accent rule, no box. */
  .notice {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-top: 30px;
    padding: 2px 0 2px 16px;
    border-left: 2px solid var(--accent);
    font-size: 16px;
    line-height: 1.55;
    color: var(--text-secondary);
  }

  .notice b {
    font-weight: 600;
    color: var(--text-primary);
  }

  .notice > svg {
    margin-top: 3px;
    color: var(--accent);
  }

  .ticket {
    margin-top: 30px;
  }

  .join {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 10px;
  }

  .joinLink {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
    font-size: 15px;
    color: var(--accent);
    text-decoration: underline;
    text-decoration-color: rgba(var(--accent-rgb), 0.4);
    text-underline-offset: 3px;
  }

  .pass {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 34px;
    padding: 0 12px;
    border-radius: 8px;
    border: 1px solid var(--field-line);
    background: transparent;
    font: inherit;
    font-size: 13.5px;
    font-variant-numeric: tabular-nums;
    color: var(--text-secondary);
    cursor: pointer;
  }

  .ledger {
    margin: -1px 0 0 160px;
    border-bottom: var(--line-strong);
  }

  .lline {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    padding: 10px 0;
    border-top: 1px solid var(--border-subtle);
    font-size: 15px;
  }

  .lwhen {
    margin-top: 3px;
    font-size: 13.5px;
    color: var(--text-tertiary);
  }

  .money {
    font-variant-numeric: tabular-nums;
    font-weight: 500;
  }

  .net {
    font-weight: 600;
  }

  .chip {
    margin-left: 8px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    color: var(--text-tertiary);
  }

  .chip::before {
    content: '· ';
  }

  .prose {
    margin-top: 44px;
  }

  .prose + .prose {
    margin-top: 40px;
  }

  /* On the shared SafeRichText, whose unlayered size would win over a layered one: it reads these. */
  .body {
    --rich-text-size: 16.5px;
    --rich-text-leading: 1.75;
    margin-top: 12px;
    color: var(--text-secondary);
    max-width: 64ch;
  }

  /* Message the studio: the message-studio piece (web-studio-sites D15) draws the block; the page spaces it. */
  .chat {
    margin-top: 48px;
  }

  /* In the side column, under the Telegram line, centered under the photo. */
  .cancel {
    margin-top: 20px;
    text-align: center;
  }

  .cancelClosed {
    margin-top: 20px;
    text-align: center;
    font-size: 15px;
    line-height: 1.55;
    color: var(--text-tertiary);
  }

  /* The booking could not be read: its words and Try again, where the heading sits. */
  .readError {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    padding: 56px 0 0;
    color: var(--text-secondary);
  }

  .problem {
    margin-top: 12px;
    text-align: center;
    font-size: 14px;
    color: var(--danger);
  }

  .side {
    position: sticky;
    top: calc(var(--head-h) + 28px);
    padding-top: 30px;
  }

  .photo {
    height: 240px;
    border-radius: 14px;
    overflow: hidden;
    background: var(--bg-card-2);
  }

  .errand {
    margin-top: 20px;
  }

  .sideNote {
    margin-top: 16px;
    display: flex;
    gap: 10px;
    font-size: 13.5px;
    line-height: 1.5;
    color: var(--text-tertiary);
  }

  .sideNote > svg {
    flex-shrink: 0;
    margin-top: 1px;
    color: var(--accent);
  }

  [data-look='light'] .with b,
  [data-look='light'] .joinLink {
    color: var(--accent-hot);
  }

  @media (max-width: 1023px) {
    .head {
      flex-direction: column;
      align-items: flex-start;
    }

    .when {
      text-align: left;
    }

    .grid {
      grid-template-columns: 1fr;
      gap: 40px;
    }

    .side {
      position: static;
    }

    .ledger {
      margin-left: 0;
    }
  }

  /* web-studio-sites D12: a booking's page on a custom site (telify-pages.html .bdHead, .prose and .bdGrid): no label
     above the name or the notes (Gabriel 2026-10-25), the notes under headings; the side column narrower at 1024 pixels;
     at 860 one column, the photo, why the booking waits and its one action first, then the ticket, the notes, Message the
     studio, and cancel last. The page's bottom space is her pages' (.tp), here and under the Complete step. */
  :global(:root[data-custom-site]) .page {
    padding-bottom: 120px;
  }

  :global(:root[data-custom-site]) .kicker {
    display: none;
  }

  :global(:root[data-custom-site]) .h1 {
    margin-top: 0;
  }

  :global(:root[data-custom-site]) .proseHead {
    display: block;
    margin-bottom: 12px;
    font-family: var(--font-display);
    font-weight: 500;
    font-size: 28px;
    letter-spacing: -0.01em;
    line-height: 1.15;
    text-transform: none;
    color: var(--text-primary);
  }

  :global(:root[data-custom-site]) .proseHead::before {
    display: none;
  }

  @media (max-width: 1024px) {
    :global(:root[data-custom-site]) .grid {
      grid-template-columns: minmax(0, 1fr) 300px;
      gap: 48px;
    }
  }

  @media (max-width: 860px) {
    :global(:root[data-custom-site]) .page {
      padding-bottom: 80px;
    }

    :global(:root[data-custom-site]) .head {
      gap: 18px;
      margin-top: 22px;
      padding-bottom: 24px;
    }

    :global(:root[data-custom-site]) .h1 {
      font-size: 40px;
    }

    :global(:root[data-custom-site]) .when {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 14px;
      padding-bottom: 0;
    }

    :global(:root[data-custom-site]) .when b {
      font-size: 24px;
    }

    :global(:root[data-custom-site]) .st {
      margin-top: 0;
    }

    :global(:root[data-custom-site]) .grid {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 0;
      margin-top: 24px;
    }

    :global(:root[data-custom-site]) .grid > div,
    :global(:root[data-custom-site]) .side {
      display: contents;
    }

    :global(:root[data-custom-site]) .photo {
      order: 1;
    }

    :global(:root[data-custom-site]) .notice {
      order: 2;
      margin: 18px 0 0;
    }

    :global(:root[data-custom-site]) .errand {
      order: 3;
      margin-top: 18px;
    }

    :global(:root[data-custom-site]) .ticket {
      order: 4;
      margin-top: 24px;
    }

    :global(:root[data-custom-site]) .prose {
      order: 5;
      margin-top: 36px;
    }

    :global(:root[data-custom-site]) .chat {
      order: 6;
      margin-top: 40px;
    }

    :global(:root[data-custom-site]) .sideNote {
      order: 7;
      margin-top: 30px;
    }

    :global(:root[data-custom-site]) :is(.cancel, .cancelClosed, .problem) {
      order: 8;
      margin-top: 22px;
    }
  }
}
